import { Box, Typography, Switch, Tooltip, Chip } from "@mui/material";
import { useState } from "react";
import * as React from 'react'
import { createNotificationConfig } from "../../helpers/UtilityHelper";
import Confirm from "./confirm";
import { apiConstants, postGeneric } from "../../common/constants";
import { useSnackbar } from "notistack";
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER } from '../../styles/foundation/colors';

const ConfigCard = ({ title, isActive, description, price, onToggle, type, data }) => {
    const [openDialog, setOpenDialog] = useState(false);

    const handleToggleClick = () => {
        setOpenDialog(true);
    };

    const handleConfirm = () => {
        onToggle(type);
        setOpenDialog(false);
    };

    const getDefaultPrice = () => {
        const notificationConfig = createNotificationConfig(data);
        if (type === 'ndrwhatsapp') {
            return notificationConfig["whatsappAutomatedPerResolvedNdr"]?.defaultPriceValue ||
                notificationConfig["whatsappAutomatedPerFailedDeliveryNdr"]?.defaultPriceValue ||
                notificationConfig["whatsappAutomatedPerAwbNdr"]?.defaultPriceValue;
        } else if (type === 'ndrcalling') {
            return notificationConfig["whatsappNdrCallingperAwbCall"]?.defaultPriceValue ||
                notificationConfig["whatsappNdrCallingperConnectedCall"]?.defaultPriceValue ||
                notificationConfig["whatsappNdrCallingperResolvedCall"]?.defaultPriceValue;
        }
        return null;
    };

    const hasConfigValue = () => {
        const notificationConfig = createNotificationConfig(data);
        if (type === 'ndrwhatsapp') {
            return notificationConfig["whatsappAutomatedPerResolvedNdr"]?.configValue ||
                notificationConfig["whatsappAutomatedPerFailedDeliveryNdr"]?.configValue ||
                notificationConfig["whatsappAutomatedPerAwbNdr"]?.configValue;
        } else if (type === 'ndrcalling') {
            return notificationConfig["whatsappNdrCallingperAwbCall"]?.configValue ||
                notificationConfig["whatsappNdrCallingperConnectedCall"]?.configValue ||
                notificationConfig["whatsappNdrCallingperResolvedCall"]?.configValue;
        }
        return false;
    };

    return (
        <>
            <Box sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: '10px',
                bgcolor: '#fff',
                p: 2.5,
                mt: type === 'ndrwhatsapp' ? 0 : 1.5,
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY }}>{title}</Typography>
                            <Chip
                                label={isActive ? "Active" : "Inactive"}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '11px',
                                    fontFamily: FONT,
                                    fontWeight: WEIGHT.semibold,
                                    bgcolor: isActive ? '#e8f5e9' : '#fce4ec',
                                    color: isActive ? '#2e7d32' : '#c62828',
                                    border: 'none',
                                }}
                            />
                        </Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE }}>{description}</Typography>
                        {isActive && price && (
                            <Box sx={{ mt: 1 }}>{price}</Box>
                        )}
                    </Box>
                    <Tooltip title={isActive ? "Disable" : "Enable"}>
                        <Switch
                            checked={isActive}
                            onChange={handleToggleClick}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }}
                        />
                    </Tooltip>
                </Box>
            </Box>
            <Confirm
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                isActive={isActive}
                title={title}
                hasConfigValue={hasConfigValue}
                getDefaultPrice={getDefaultPrice}
                handleConfirm={handleConfirm}
                configType={type === 'ndrwhatsapp' ? 'NDR_WHATSAPP' : 'NDR_CALLING'}
                selectedConfig={data}
            />
        </>
    );
};

function AutomatedNdrConfig({ data, merchantId, setReload }) {
    const { enqueueSnackbar } = useSnackbar();
    const configMap = createNotificationConfig(data);
    const isNDRActive = configMap["whatsappAutomatedPerResolvedNdr"]?.isActive || false;
    const isNDRCallingActive = configMap["whatsappNdrCallingperAwbCall"]?.isActive || false;

    const toggleStatus = async (type) => {
        try {
            if (type === 'ndrwhatsapp') {
                const res = await postGeneric(
                    `${apiConstants.MERCHANT_DETAILS}/${merchantId}/automatedNDR?flag=${!isNDRActive}`
                );
                if (res?.status === 200) {
                    enqueueSnackbar("Success", { variant: 'success' });
                    setReload(prev => prev + 1);
                } else {
                    throw new Error(res.data?.errorMsg || "Some error occurred");
                }
            } else if (type === 'ndrcalling') {
                const res = await postGeneric(
                    `${apiConstants.FUTWORKS_CONFIG}?merchantOid=${merchantId}`,
                    { push_lead: !isNDRCallingActive }
                );
                if (res.data?.status === "SUCCESS") {
                    enqueueSnackbar("Success", { variant: 'success' });
                    setReload(prev => prev + 1);
                } else {
                    throw new Error(res.data?.errorMsg || "Some error occurred");
                }
            }
        } catch (error) {
            enqueueSnackbar(error.message || "Some error occurred", { variant: 'error' });
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY, mb: 1.5 }}>
                Automated NDR
            </Typography>
            <ConfigCard
                title="Automated NDR WhatsApp"
                isActive={isNDRActive}
                description="Automatically process NDR cases through WhatsApp"
                price={isNDRActive && getPriceDisplay(data, 'whatsapp')}
                onToggle={toggleStatus}
                type="ndrwhatsapp"
                data={data}
            />
            <ConfigCard
                title="Automated NDR Calling"
                isActive={isNDRCallingActive}
                description="Automatically process NDR cases through Calling"
                price={isNDRCallingActive && getPriceDisplay(data, 'calling')}
                onToggle={toggleStatus}
                type="ndrcalling"
                data={data}
            />
        </Box>
    );
}

export default AutomatedNdrConfig;

const getPriceDisplay = (data, type) => {
    const notificationConfig = createNotificationConfig(data);

    const configKeys = {
        whatsapp: [
            { key: 'whatsappAutomatedPerResolvedNdr', label: 'Cost per resolved NDR: ' },
            { key: 'whatsappAutomatedPerFailedDeliveryNdr', label: 'Cost per failed delivery: ' },
            { key: 'whatsappAutomatedPerAwbNdr', label: 'Cost per AWB: ' }
        ],
        calling: [
            { key: 'whatsappNdrCallingperAwbCall', label: 'Cost per AWB Call: ' },
            { key: 'whatsappNdrCallingperConnectedCall', label: 'Cost per Connected Call: ' },
            { key: 'whatsappNdrCallingperResolvedCall', label: 'Cost per Resolved Call: ' }
        ]
    };

    const defaultLabels = {
        whatsapp: 'Default Automated NDR WA Price: ',
        calling: 'Default Automated NDR Calling Price: '
    };

    const selectedKeys = configKeys[type] || [];
    let priceLabel = 'Cost: ';
    let price = '0';

    const configItem = selectedKeys.find(({ key }) => notificationConfig[key]?.configValue);
    const defaultItem = selectedKeys.find(({ key }) => notificationConfig[key]?.defaultPriceValue);

    if (configItem) {
        priceLabel = configItem.label;
        price = notificationConfig[configItem.key].configValue;
    } else if (defaultItem) {
        priceLabel = defaultLabels[type];
        price = notificationConfig[defaultItem.key].defaultPriceValue;
    }

    return (
        <>
            <Typography component="span" sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE }}>
                {priceLabel}
            </Typography>
            <Typography component="span" sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>
                ₹{price}
            </Typography>
        </>
    );
};
